import { memo, useCallback } from 'react';

import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { PrivateWorkspaceAgentTableMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/PrivateWorkspaceAgentTableMoniker';
import { PrivateWorkspaceAgentTableMonikerFrame } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/PrivateWorkspaceAgentTableMonikerFrame';
import { type WorkspaceAgentTableMonikerSegment } from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/WorkspaceAgentTableCellContent';
import {
  WorkspaceAgentTablePill,
  type WorkspaceAgentTablePillBodyFrameProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/table-moniker/WorkspaceAgentTablePill';
import { usePrivateWorkspaceAgentHydratedArtifactPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentHydratedArtifactPill';
import { type TablePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';

export type PrivateWorkspaceAgentTablePillChipProps = {
  isStreaming: boolean;
  pill: TablePill;
};

export const PrivateWorkspaceAgentTablePillChip = memo(
  ({ isStreaming, pill }: PrivateWorkspaceAgentTablePillChipProps) => {
    const {
      inViewRef,
      isPending,
      isUnavailable,
      pill: hydrated,
      tablePaging,
    } = usePrivateWorkspaceAgentHydratedArtifactPill({
      isStreaming,
      pill,
    });

    const renderBodyFrame = useCallback(
      ({ children, visibleRows }: WorkspaceAgentTablePillBodyFrameProps) => (
        <PrivateWorkspaceAgentTableMonikerFrame visibleRows={visibleRows}>
          {children}
        </PrivateWorkspaceAgentTableMonikerFrame>
      ),
      [],
    );

    const renderMoniker = useCallback(
      (segment: WorkspaceAgentTableMonikerSegment) => (
        <PrivateWorkspaceAgentTableMoniker isStreaming={isStreaming} segment={segment} />
      ),
      [isStreaming],
    );

    if (isPending) {
      return (
        <div className="w-full" ref={inViewRef}>
          <WorkspaceAgentPendingPill label="Table" />
        </div>
      );
    }

    if (isUnavailable || hydrated.type !== 'table') {
      return (
        <div className="w-full" ref={inViewRef}>
          <WorkspaceAgentUnavailablePill kind="reference" referenceId={pill.id} />
        </div>
      );
    }

    return (
      <WorkspaceAgentTablePill
        hasMore={tablePaging?.hasMore}
        isFetchNextPageError={tablePaging?.isFetchNextPageError}
        isLoadingMore={tablePaging?.isLoadingMore}
        onLoadMore={tablePaging?.loadMore}
        onRetryLoadMore={tablePaging?.retryLoadMore}
        pill={hydrated}
        renderBodyFrame={renderBodyFrame}
        renderMoniker={renderMoniker}
      />
    );
  },
);

PrivateWorkspaceAgentTablePillChip.displayName = 'PrivateWorkspaceAgentTablePillChip';
